/*
 * Copyright (C) 2026 Nex4Nova (Geoblast group)
 * SPDX-License-Identifier: Proprietary
 */
import './DataInstructions.css';
import { useLanguage } from '../../contexts/LanguageContext.jsx';

function DataInstructions() {
  const { t } = useLanguage();

  return (
    <div className="data-instructions">
      <div className="data-instructions-content">
        <h1>{t('data.title')}</h1>

        <p className="lead">
          {t('data.lead', {
            openFolder: <em>{t('data.leadOpen')}</em>,
            parquet: <code>.parquet</code>,
            csv: <code>.csv</code>,
          })}
        </p>

        <h2>{t('data.folderLayout')}</h2>
        <pre className="folder-listing">{`my-project/
├── collars.{parquet,csv}                  (required)
├── survey.{parquet,csv}                   (needed for 3D)
├── assays.{parquet,csv}                   (drives "colour by" in 3D + Strip Log)
├── geology.{parquet,csv}                  (categorical colour-by)
├── structure.{parquet,csv}                (structural discs in 3D)
├── surface_samples.{parquet,csv}          (out-of-hole sample points
│                                              consumed by the Analytics view)
└── precomputed_desurveyed.{parquet,csv}   (optional — skip live desurvey)`}</pre>
        <p>
          {t('data.onlyRequired', { collars: <code>collars</code> })}
        </p>

        <h2>{t('data.canonicalSchema')}</h2>
        <p>
          {t('data.schemaLead', {
            standardizeColumns: <code>standardizeColumns</code>,
            lat: <code>lat</code>,
            latitude: <code>latitude</code>,
            md: <code>md</code>,
            depth: <code>depth</code>,
          })}
        </p>

        <table className="schema-table">
          <thead>
            <tr>
              <th>{t('data.file')}</th>
              <th>{t('data.requiredColumns')}</th>
              <th>{t('data.optionalColumns')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>collars</code></td>
              <td><code>hole_id</code>, <code>latitude</code>, <code>longitude</code></td>
              <td><code>elevation</code>, <code>datasource_hole_id</code>, <code>project_id</code>, <code>hole_type</code>, <code>max_depth</code></td>
            </tr>
            <tr>
              <td><code>survey</code></td>
              <td><code>hole_id</code>, <code>depth</code>, <code>azimuth</code>, <code>dip</code></td>
              <td>—</td>
            </tr>
            <tr>
              <td><code>assays</code></td>
              <td><code>hole_id</code>, <code>from</code>, <code>to</code></td>
              <td>{t('data.optAssays', { au: <code>Au_PPM</code>, cu: <code>Cu_PCT</code> })}</td>
            </tr>
            <tr>
              <td><code>geology</code></td>
              <td><code>hole_id</code>, <code>from</code>, <code>to</code></td>
              <td>{t('data.optGeology', { geoCode: <code>geology_code</code>, geoDesc: <code>geology_description</code> })}</td>
            </tr>
            <tr>
              <td><code>structure</code></td>
              <td><code>hole_id</code>, <code>depth</code>, <code>dip</code>, <code>azimuth</code></td>
              <td>{t('data.optStructure', { alpha: <code>alpha</code>, beta: <code>beta</code>, strike: <code>strike</code> })}</td>
            </tr>
            <tr>
              <td><code>surface_samples</code></td>
              <td><code>sample_id</code>, <code>surface_sample_type</code> + either (<code>latitude</code>, <code>longitude</code>) or (<code>easting</code>, <code>northing</code>, <code>crs</code>)</td>
              <td>{t('data.optSurface', { elevation: <code>elevation</code>, sourceId: <code>datasource_surface_sample_id</code>, reportNumber: <code>report_number</code>, projectId: <code>project_id</code>, au: <code>Au_PPM</code>, cu: <code>Cu_PCT</code> })}</td>
            </tr>
            <tr>
              <td><code>precomputed_desurveyed</code></td>
              <td><code>hole_id</code>, <code>x</code>, <code>y</code>, <code>z</code>, <code>md</code></td>
              <td>{t('data.optPrecomputed')}</td>
            </tr>
          </tbody>
        </table>

        <p>
          {t('data.joinKey', { holeId: <code>hole_id</code> })}
        </p>
        <p>
          {t('data.geologyFlexible', { geologyCsv: <code>geology.csv</code> })}
        </p>

        <h2>{t('data.projection')}</h2>
        <p>
          {t('data.projectionDesc', { precomputed: <code>precomputed_desurveyed</code> })}
        </p>
        <p>
          {t('data.projectionDesc2')}
        </p>
      </div>
    </div>
  );
}

export default DataInstructions;
